import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Building2, User, MapPin, Phone, Mail, Globe, Clock,
  Tag, FileText, Loader2, CheckCircle, AlertCircle, ChevronRight,
  ChevronLeft, ChevronDown, MessageSquare, DollarSign, Briefcase,
  Calendar, Save, AlertTriangle, Package, Search, ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// SHARED WIZARD COMPONENTS
// ═══════════════════════════════════════════════════════════════

const WizardModal = ({ isOpen, onClose, title, subtitle, icon: Icon, iconColor, children, footer }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 pb-8 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="bg-[#12121a] rounded-2xl w-full max-w-lg border border-white/10 overflow-hidden max-h-[calc(100vh-8rem)] flex flex-col my-auto"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", iconColor || "bg-primary/20")}>
                <Icon className={cn("w-5 h-5", iconColor ? iconColor.replace('bg-', 'text-').replace('/20', '-400') : "text-primary")} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{title}</h2>
                <p className="text-sm text-gray-400">{subtitle}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {children}
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/10 flex justify-between items-center sticky bottom-0 bg-[#12121a]">
            {footer}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const FormField = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const TextInput = ({ icon: Icon, value, onChange, placeholder, type = 'text', ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />}
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary/50",
        Icon ? "pl-10 pr-4" : "px-4"
      )}
      {...props}
    />
  </div>
);

const TextArea = ({ icon: Icon, value, onChange, placeholder, rows = 3, maxLength, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-3 w-4 h-4 text-gray-500" />}
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className={cn(
        "w-full py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 resize-none",
        Icon ? "pl-10 pr-4" : "px-4"
      )}
      {...props}
    />
    {maxLength && (
      <div className="absolute bottom-2 right-3 text-xs text-gray-500">
        {value?.length || 0}/{maxLength}
      </div>
    )}
  </div>
);

const SelectInput = ({ value, onChange, options, placeholder, ...props }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-primary/50"
    {...props}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(opt => (
      <option key={opt.value || opt} value={opt.value || opt}>
        {opt.label || opt}
      </option>
    ))}
  </select>
);

const AdvancedSection = ({ children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-t border-white/5 pt-4 mt-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
      >
        <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
        Advanced Options
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4 space-y-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Helper to get auth headers
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

// Helper for auto-save
const useAutoSave = (formData, wizardType, conversationId, draftId, enabled = true) => {
  const timeoutRef = useRef(null);
  const lastSavedRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  const save = useCallback(async () => {
    if (!enabled) return;
    
    const dataStr = JSON.stringify(formData);
    if (dataStr === lastSavedRef.current) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/wizard-drafts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          id: draftId,
          wizard_type: wizardType,
          conversation_id: conversationId,
          form_data_json: formData
        })
      });
      
      if (response.ok) {
        lastSavedRef.current = dataStr;
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    } finally {
      setIsSaving(false);
    }
  }, [formData, wizardType, conversationId, draftId, enabled]);
  
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(save, 3000); // Auto-save after 3 seconds of inactivity
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [formData, save]);
  
  return { isSaving, lastSaved, saveNow: save };
};


// ═══════════════════════════════════════════════════════════════
// 0) SEARCH CONTACT WIZARD - With Contact Detail Popup
// ═══════════════════════════════════════════════════════════════

export function SearchContactWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetails, setContactDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(
        `${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(query)}&limit=15`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search contacts');
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadContactDetails = async (contact) => {
    setSelectedContact(contact);
    setLoadingDetails(true);
    
    try {
      // Load full contact details from GHL including opportunities
      const response = await fetch(
        `${API_URL}/api/wizards/crm/contact/${contact.id}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setContactDetails(data.contact);
        } else {
          // Use the contact info we already have
          setContactDetails(contact);
          toast.error('Could not load full details');
        }
      } else {
        setContactDetails(contact);
      }
    } catch (error) {
      console.error('Failed to load contact details:', error);
      setContactDetails(contact);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetails = () => {
    setSelectedContact(null);
    setContactDetails(null);
  };

  const openInCRM = () => {
    if (contactDetails?.id && contactDetails?.locationId) {
      // One Force Live CRM URL - connects to client's sub-account
      const crmUrl = `https://app.oneforcelive.io/v2/location/${contactDetails.locationId}/contacts/detail/${contactDetails.id}`;
      window.open(crmUrl, '_blank');
    } else if (contactDetails?.id) {
      toast.info('Location ID not available - contact your admin for CRM access');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  return (
    <>
      <WizardModal
        isOpen={isOpen && !selectedContact}
        onClose={onClose}
        title="Search Contact"
        subtitle="Find and view contact details"
        icon={Search}
        iconColor="bg-cyan-500/20"
        footer={
          <>
            <div className="text-sm text-gray-400">
              {searchResults.length > 0 ? `${searchResults.length} contacts found` : 'Enter at least 2 characters'}
            </div>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Search by name, email, or phone" hint="Results appear as you type">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search contacts..."
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                autoFocus
                data-testid="search-contact-input"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400 animate-spin" />
              )}
            </div>
          </FormField>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {searchResults.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => loadContactDetails(contact)}
                  className="w-full p-3 rounded-xl bg-white/5 hover:bg-cyan-500/20 border border-white/10 hover:border-cyan-500/30 text-left transition-all group"
                  data-testid={`contact-result-${contact.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">
                        {contact.firstName} {contact.lastName}
                      </p>
                      <p className="text-sm text-gray-400 truncate">
                        {contact.email || contact.phone || 'No contact info'}
                      </p>
                      {contact.companyName && (
                        <p className="text-xs text-gray-500 truncate">{contact.companyName}</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-cyan-400 transition-colors flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
            <div className="text-center py-8 text-gray-400">
              <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No contacts found matching "{searchQuery}"</p>
            </div>
          )}
        </div>
      </WizardModal>

      {/* Contact Details Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 pb-8 overflow-y-auto" onClick={closeDetails}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={e => e.stopPropagation()}
            className="bg-[#12121a] border border-cyan-500/30 rounded-2xl max-w-2xl w-full max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-auto"
            data-testid="contact-details-modal"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 p-5 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-2xl font-bold text-white">
                    {contactDetails?.firstName?.[0] || '?'}{contactDetails?.lastName?.[0] || ''}
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">
                      {contactDetails?.firstName || ''} {contactDetails?.lastName || ''}
                    </h2>
                    <p className="text-sm text-gray-400">
                      {contactDetails?.companyName || 'Individual Contact'}
                    </p>
                    {contactDetails?.type && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs capitalize">
                        {contactDetails.type}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={closeDetails} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {loadingDetails ? (
              <div className="p-8 text-center flex-1">
                <Loader2 className="w-8 h-8 mx-auto text-cyan-400 animate-spin mb-3" />
                <p className="text-gray-400">Loading contact details...</p>
              </div>
            ) : (
              <div className="p-5 overflow-y-auto flex-1">
                {/* Contact Info Section */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {/* Primary Info */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-cyan-400" />
                      Contact Information
                    </h3>
                    <div className="space-y-3">
                      {contactDetails?.email && (
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <a href={`mailto:${contactDetails.email}`} className="text-cyan-400 hover:underline text-sm truncate">
                            {contactDetails.email}
                          </a>
                        </div>
                      )}
                      {contactDetails?.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <a href={`tel:${contactDetails.phone}`} className="text-cyan-400 hover:underline text-sm">
                            {contactDetails.phone}
                          </a>
                        </div>
                      )}
                      {contactDetails?.website && (
                        <div className="flex items-center gap-3">
                          <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <a href={contactDetails.website} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline text-sm truncate">
                            {contactDetails.website}
                          </a>
                        </div>
                      )}
                      {(contactDetails?.address1 || contactDetails?.city) && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300 text-sm">
                            {contactDetails.address1}
                            {contactDetails.city && `, ${contactDetails.city}`}
                            {contactDetails.state && `, ${contactDetails.state}`}
                            {contactDetails.postalCode && ` ${contactDetails.postalCode}`}
                          </span>
                        </div>
                      )}
                      {!contactDetails?.email && !contactDetails?.phone && (
                        <p className="text-gray-500 text-sm">No contact information available</p>
                      )}
                    </div>
                  </div>

                  {/* Pipeline/Stage Info */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-amber-400" />
                      Pipeline Status
                    </h3>
                    <div className="space-y-3">
                      {contactDetails?.pipelineStage ? (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Stage:</span>
                            <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium">
                              {contactDetails.pipelineStage}
                            </span>
                          </div>
                          {contactDetails?.pipelineName && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-400 text-sm">Pipeline:</span>
                              <span className="text-white text-sm">{contactDetails.pipelineName}</span>
                            </div>
                          )}
                          {contactDetails?.totalOpportunityValue > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-400 text-sm">Total Value:</span>
                              <span className="text-green-400 font-medium text-sm">
                                {formatCurrency(contactDetails.totalOpportunityValue)}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500 text-sm">No pipeline assigned</p>
                      )}
                      
                      {/* Tags */}
                      {contactDetails?.tags && contactDetails.tags.length > 0 && (
                        <div className="pt-2 border-t border-white/10">
                          <span className="text-gray-400 text-sm block mb-2">Tags:</span>
                          <div className="flex flex-wrap gap-1">
                            {contactDetails.tags.slice(0, 6).map((tag, i) => (
                              <span key={i} className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-xs">
                                {tag}
                              </span>
                            ))}
                            {contactDetails.tags.length > 6 && (
                              <span className="text-gray-500 text-xs">+{contactDetails.tags.length - 6} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Opportunities Section */}
                {contactDetails?.opportunities && contactDetails.opportunities.length > 0 && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      Opportunities ({contactDetails.opportunities.length})
                    </h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {contactDetails.opportunities.map((opp, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{opp.name}</p>
                            <p className="text-xs text-gray-400">{opp.pipelineName} → {opp.stageName}</p>
                          </div>
                          <div className="text-right ml-3">
                            <p className="text-green-400 font-medium text-sm">{formatCurrency(opp.monetaryValue)}</p>
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded-full",
                              opp.status === 'open' ? 'bg-blue-500/20 text-blue-400' :
                              opp.status === 'won' ? 'bg-green-500/20 text-green-400' :
                              opp.status === 'lost' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            )}>
                              {opp.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Fields */}
                {contactDetails?.customFields && Object.keys(contactDetails.customFields).length > 0 && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-400" />
                      Custom Fields
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3 max-h-32 overflow-y-auto">
                      {Object.entries(contactDetails.customFields).slice(0, 10).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-start text-sm">
                          <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}:</span>
                          <span className="text-white text-right ml-2 truncate max-w-[150px]">{String(value) || '-'}</span>
                        </div>
                      ))}
                    </div>
                    {Object.keys(contactDetails.customFields).length > 10 && (
                      <p className="text-xs text-gray-500 mt-2">+{Object.keys(contactDetails.customFields).length - 10} more fields</p>
                    )}
                  </div>
                )}

                {/* Activity/Timeline */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-green-400" />
                    Activity
                  </h3>
                  <div className="space-y-2 text-sm">
                    {contactDetails?.dateAdded && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Added:</span>
                        <span className="text-white">{new Date(contactDetails.dateAdded).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                    {contactDetails?.dateUpdated && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Last Updated:</span>
                        <span className="text-white">{new Date(contactDetails.dateUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                    {contactDetails?.source && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Source:</span>
                        <span className="text-white">{contactDetails.source}</span>
                      </div>
                    )}
                    {contactDetails?.id && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Contact ID:</span>
                        <span className="text-gray-500 font-mono text-xs">{contactDetails.id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className="p-4 border-t border-white/10 bg-black/30">
              <div className="flex gap-3">
                <button
                  onClick={openInCRM}
                  disabled={!contactDetails?.locationId}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="open-in-crm-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in CRM
                </button>
                <button
                  onClick={closeDetails}
                  className="py-3 px-6 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// 1) CREATE CONTACT WIZARD
// ═══════════════════════════════════════════════════════════════

export function CreateContactWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    company: initialData.company || '',
    address: initialData.address || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zip: initialData.zip || '',
    tags: initialData.tags || '',
    source: initialData.source || 'AGI Agent',
    notes: initialData.notes || '',
    customFields: initialData.customFields || '',
  });
  
  const { isSaving, lastSaved } = useAutoSave(formData, 'CRM.CREATE_CONTACT', conversationId, initialData.draftId);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.firstName.trim() && !formData.lastName.trim() && !formData.company.trim()) {
      toast.error('Please provide a name or company');
      return;
    }
    if (!formData.email.trim() && !formData.phone.trim()) {
      toast.warning('Recommend providing email or phone for contact');
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/contact`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          first_name: formData.firstName || undefined,
          last_name: formData.lastName || undefined,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          company_name: formData.company || undefined,
          address: formData.address || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postal_code: formData.zip || undefined,
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
          source: formData.source || 'AGI Agent',
          custom_fields: formData.customFields ? JSON.parse(formData.customFields) : undefined
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Contact created: ${formData.firstName} ${formData.lastName}`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || result.detail || 'Failed to create contact');
      }
    } catch (error) {
      toast.error('Error creating contact: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Contact"
      subtitle="Add a new contact to your CRM"
      icon={User}
      iconColor="bg-cyan-500/20"
      footer={
        <>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {isSaving && <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</>}
            {lastSaved && !isSaving && <><Save className="w-3 h-3" /> Draft saved</>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Create Contact
            </Button>
          </div>
        </>
      }
    >
      {/* Name Row */}
      <div className="grid grid-cols-2 gap-4">
        <FormField label="First Name" required>
          <TextInput
            value={formData.firstName}
            onChange={v => handleChange('firstName', v)}
            placeholder="John"
            data-testid="contact-first-name"
          />
        </FormField>
        <FormField label="Last Name" required>
          <TextInput
            value={formData.lastName}
            onChange={v => handleChange('lastName', v)}
            placeholder="Smith"
            data-testid="contact-last-name"
          />
        </FormField>
      </div>
      
      {/* Contact Info */}
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Email">
          <TextInput
            icon={Mail}
            type="email"
            value={formData.email}
            onChange={v => handleChange('email', v)}
            placeholder="john@example.com"
            data-testid="contact-email"
          />
        </FormField>
        <FormField label="Phone">
          <TextInput
            icon={Phone}
            type="tel"
            value={formData.phone}
            onChange={v => handleChange('phone', v)}
            placeholder="(555) 123-4567"
            data-testid="contact-phone"
          />
        </FormField>
      </div>
      
      <FormField label="Company">
        <TextInput
          icon={Building2}
          value={formData.company}
          onChange={v => handleChange('company', v)}
          placeholder="Company name"
          data-testid="contact-company"
        />
      </FormField>
      
      <FormField label="Tags" hint="Comma-separated list of tags">
        <TextInput
          icon={Tag}
          value={formData.tags}
          onChange={v => handleChange('tags', v)}
          placeholder="Lead, Hot, VIP"
          data-testid="contact-tags"
        />
      </FormField>
      
      <FormField label="Source">
        <SelectInput
          value={formData.source}
          onChange={v => handleChange('source', v)}
          options={['AGI Agent', 'Website', 'Referral', 'Cold Call', 'Event', 'Social Media', 'Other']}
          data-testid="contact-source"
        />
      </FormField>
      
      <AdvancedSection>
        <FormField label="Address">
          <TextInput
            icon={MapPin}
            value={formData.address}
            onChange={v => handleChange('address', v)}
            placeholder="123 Main St"
            data-testid="contact-address"
          />
        </FormField>
        
        <div className="grid grid-cols-3 gap-3">
          <FormField label="City">
            <TextInput
              value={formData.city}
              onChange={v => handleChange('city', v)}
              placeholder="City"
              data-testid="contact-city"
            />
          </FormField>
          <FormField label="State">
            <TextInput
              value={formData.state}
              onChange={v => handleChange('state', v)}
              placeholder="TX"
              maxLength={2}
              className="uppercase"
              data-testid="contact-state"
            />
          </FormField>
          <FormField label="ZIP">
            <TextInput
              value={formData.zip}
              onChange={v => handleChange('zip', v)}
              placeholder="79701"
              data-testid="contact-zip"
            />
          </FormField>
        </div>
        
        <FormField label="Custom Fields (JSON)" hint='e.g., [{"key": "field_id", "value": "value"}]'>
          <TextArea
            value={formData.customFields}
            onChange={v => handleChange('customFields', v)}
            placeholder='[{"key": "custom_field_1", "value": "some value"}]'
            rows={2}
            data-testid="contact-custom-fields"
          />
        </FormField>
      </AdvancedSection>
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 1b) UPDATE CONTACT WIZARD
// ═══════════════════════════════════════════════════════════════

export function UpdateContactWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(initialData.selectedContact || null);
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    company: initialData.company || '',
    address: initialData.address || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zip: initialData.zip || '',
    tagsToAdd: initialData.tagsToAdd || '',
    tagsToRemove: initialData.tagsToRemove || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const searchContacts = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(searchQuery)}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
      }
    } catch (error) {
      toast.error('Error searching contacts');
    } finally {
      setSearching(false);
    }
  };
  
  // When contact is selected, populate form with their data
  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setFormData({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email || '',
      phone: contact.phone || '',
      company: contact.companyName || '',
      address: '',
      city: '',
      state: '',
      zip: '',
      tagsToAdd: '',
      tagsToRemove: '',
    });
  };

  const handleSubmit = async () => {
    if (!selectedContact) {
      toast.error('Please select a contact to update');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/contact/${selectedContact.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          first_name: formData.firstName || undefined,
          last_name: formData.lastName || undefined,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          company_name: formData.company || undefined,
          address: formData.address || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postal_code: formData.zip || undefined,
          tags_to_add: formData.tagsToAdd ? formData.tagsToAdd.split(',').map(t => t.trim()).filter(Boolean) : [],
          tags_to_remove: formData.tagsToRemove ? formData.tagsToRemove.split(',').map(t => t.trim()).filter(Boolean) : [],
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Contact ${formData.firstName} ${formData.lastName} updated`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || result.detail || 'Failed to update contact');
      }
    } catch (error) {
      toast.error('Error updating contact: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Contact"
      subtitle="Update an existing contact in your CRM"
      icon={User}
      iconColor="bg-cyan-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !selectedContact}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Update Contact
            </Button>
          </div>
        </>
      }
    >
      {/* Contact Search */}
      <FormField label="Find Contact" required>
        <div className="flex gap-2">
          <TextInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name, email, or phone..."
            data-testid="update-contact-search"
          />
          <Button variant="outline" onClick={searchContacts} disabled={searching}>
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
      </FormField>
      
      {searchResults.length > 0 && (
        <div className="border border-white/10 rounded-xl overflow-hidden max-h-32 overflow-y-auto">
          {searchResults.map(contact => (
            <button
              key={contact.id}
              type="button"
              onClick={() => handleContactSelect(contact)}
              className={cn(
                "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                selectedContact?.id === contact.id && "bg-primary/10"
              )}
            >
              <User className="w-4 h-4 text-gray-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">
                  {contact.firstName} {contact.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {contact.email || contact.phone}
                </p>
              </div>
              {selectedContact?.id === contact.id && (
                <CheckCircle className="w-4 h-4 text-primary" />
              )}
            </button>
          ))}
        </div>
      )}
      
      {selectedContact && (
        <>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20 mb-2">
            <User className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary">
              Editing: {selectedContact.firstName} {selectedContact.lastName}
            </span>
          </div>
          
          {/* Editable Fields */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="First Name">
              <TextInput
                value={formData.firstName}
                onChange={v => handleChange('firstName', v)}
                placeholder="John"
                data-testid="update-contact-first-name"
              />
            </FormField>
            <FormField label="Last Name">
              <TextInput
                value={formData.lastName}
                onChange={v => handleChange('lastName', v)}
                placeholder="Smith"
                data-testid="update-contact-last-name"
              />
            </FormField>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Email">
              <TextInput
                icon={Mail}
                type="email"
                value={formData.email}
                onChange={v => handleChange('email', v)}
                placeholder="john@example.com"
                data-testid="update-contact-email"
              />
            </FormField>
            <FormField label="Phone">
              <TextInput
                icon={Phone}
                type="tel"
                value={formData.phone}
                onChange={v => handleChange('phone', v)}
                placeholder="(555) 123-4567"
                data-testid="update-contact-phone"
              />
            </FormField>
          </div>
          
          <FormField label="Company">
            <TextInput
              icon={Building2}
              value={formData.company}
              onChange={v => handleChange('company', v)}
              placeholder="Company name"
              data-testid="update-contact-company"
            />
          </FormField>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Tags to Add" hint="Comma-separated">
              <TextInput
                icon={Tag}
                value={formData.tagsToAdd}
                onChange={v => handleChange('tagsToAdd', v)}
                placeholder="VIP, Hot Lead"
                data-testid="update-contact-tags-add"
              />
            </FormField>
            <FormField label="Tags to Remove" hint="Comma-separated">
              <TextInput
                icon={Tag}
                value={formData.tagsToRemove}
                onChange={v => handleChange('tagsToRemove', v)}
                placeholder="Cold, Unqualified"
                data-testid="update-contact-tags-remove"
              />
            </FormField>
          </div>
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 2) SEND SMS WIZARD
// ═══════════════════════════════════════════════════════════════

export function SendSMSWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contactMethod: initialData.contactMethod || 'phone', // 'phone', 'email', 'search'
    phone: initialData.phone || '',
    email: initialData.email || '',
    searchQuery: initialData.searchQuery || '',
    message: initialData.message || '',
    saveAsTemplate: false,
  });
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(initialData.selectedContact || null);
  const [searching, setSearching] = useState(false);
  
  const { isSaving, lastSaved } = useAutoSave(formData, 'CRM.SEND_SMS', conversationId, initialData.draftId);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const searchContacts = async () => {
    if (!formData.searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(formData.searchQuery)}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
        if (data.contacts?.length === 0) {
          toast.info('No contacts found');
        }
      }
    } catch (error) {
      toast.error('Error searching contacts');
    } finally {
      setSearching(false);
    }
  };
  
  const handleSubmit = async () => {
    // Validation
    if (!formData.message.trim()) {
      toast.error('Message is required');
      return;
    }
    
    let targetPhone = formData.phone;
    let targetEmail = formData.email;
    
    if (formData.contactMethod === 'search' && selectedContact) {
      targetPhone = selectedContact.phone;
      targetEmail = selectedContact.email;
    }
    
    if (!targetPhone && !targetEmail) {
      toast.error('Please provide a phone number or select a contact');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/sms`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          phone: targetPhone || undefined,
          email: targetEmail || undefined,
          message: formData.message,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`SMS sent to ${selectedContact?.name || targetPhone}`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || result.detail || 'Failed to send SMS');
      }
    } catch (error) {
      toast.error('Error sending SMS: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const charCount = formData.message?.length || 0;
  const segments = Math.ceil(charCount / 160) || 1;

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send SMS"
      subtitle="Send a text message to a contact"
      icon={MessageSquare}
      iconColor="bg-green-500/20"
      footer={
        <>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {segments > 1 && (
              <span className="text-amber-400">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                {segments} segments
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Send SMS
            </Button>
          </div>
        </>
      }
    >
      {/* Contact Selection Method */}
      <FormField label="Send To" required>
        <div className="flex gap-2">
          {[
            { value: 'phone', label: 'Phone Number' },
            { value: 'email', label: 'By Email' },
            { value: 'search', label: 'Search Contact' }
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleChange('contactMethod', opt.value)}
              className={cn(
                "flex-1 py-2 px-3 rounded-lg border text-sm transition-colors",
                formData.contactMethod === opt.value
                  ? "bg-primary/20 border-primary/50 text-primary"
                  : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FormField>
      
      {formData.contactMethod === 'phone' && (
        <FormField label="Phone Number" required>
          <TextInput
            icon={Phone}
            type="tel"
            value={formData.phone}
            onChange={v => handleChange('phone', v)}
            placeholder="(555) 123-4567"
            data-testid="sms-phone"
          />
        </FormField>
      )}
      
      {formData.contactMethod === 'email' && (
        <FormField label="Contact Email" required hint="We'll find the contact and send to their phone">
          <TextInput
            icon={Mail}
            type="email"
            value={formData.email}
            onChange={v => handleChange('email', v)}
            placeholder="john@example.com"
            data-testid="sms-email"
          />
        </FormField>
      )}
      
      {formData.contactMethod === 'search' && (
        <>
          <FormField label="Search Contacts">
            <div className="flex gap-2">
              <TextInput
                value={formData.searchQuery}
                onChange={v => handleChange('searchQuery', v)}
                placeholder="Search by name, email, or phone..."
                data-testid="sms-search"
              />
              <Button variant="outline" onClick={searchContacts} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </FormField>
          
          {searchResults.length > 0 && (
            <div className="border border-white/10 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
              {searchResults.map(contact => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => setSelectedContact(contact)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                    selectedContact?.id === contact.id && "bg-primary/10"
                  )}
                >
                  <User className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      {contact.firstName} {contact.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {contact.phone || contact.email}
                    </p>
                  </div>
                  {selectedContact?.id === contact.id && (
                    <CheckCircle className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
          
          {selectedContact && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20">
              <User className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary">
                Sending to: {selectedContact.firstName} {selectedContact.lastName} ({selectedContact.phone})
              </span>
            </div>
          )}
        </>
      )}
      
      <FormField label="Message" required hint={`${charCount}/160 characters per segment`}>
        <TextArea
          value={formData.message}
          onChange={v => handleChange('message', v)}
          placeholder="Type your message here..."
          rows={4}
          maxLength={1600}
          data-testid="sms-message"
        />
      </FormField>
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 3) SEND EMAIL WIZARD
// ═══════════════════════════════════════════════════════════════

export function SendEmailWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contactMethod: initialData.contactMethod || 'email',
    email: initialData.email || '',
    phone: initialData.phone || '',
    searchQuery: initialData.searchQuery || '',
    subject: initialData.subject || '',
    body: initialData.body || '',
    replyTo: initialData.replyTo || '',
  });
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(initialData.selectedContact || null);
  const [searching, setSearching] = useState(false);
  
  const { isSaving, lastSaved } = useAutoSave(formData, 'CRM.SEND_EMAIL', conversationId, initialData.draftId);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const searchContacts = async () => {
    if (!formData.searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(formData.searchQuery)}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
      }
    } catch (error) {
      toast.error('Error searching contacts');
    } finally {
      setSearching(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!formData.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!formData.body.trim()) {
      toast.error('Email body is required');
      return;
    }
    
    let targetEmail = formData.email;
    let targetPhone = formData.phone;
    
    if (formData.contactMethod === 'search' && selectedContact) {
      targetEmail = selectedContact.email;
      targetPhone = selectedContact.phone;
    }
    
    if (!targetEmail && !targetPhone) {
      toast.error('Please provide an email address or select a contact');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: targetEmail || undefined,
          phone: targetPhone || undefined,
          subject: formData.subject,
          body: formData.body,
          reply_to: formData.replyTo || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Email sent to ${selectedContact?.name || targetEmail}`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || result.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Error sending email: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Email"
      subtitle="Send an email to a contact"
      icon={Mail}
      iconColor="bg-blue-500/20"
      footer={
        <>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {isSaving && <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</>}
            {lastSaved && !isSaving && <><Save className="w-3 h-3" /> Draft saved</>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </div>
        </>
      }
    >
      {/* Contact Selection */}
      <FormField label="Send To" required>
        <div className="flex gap-2">
          {[
            { value: 'email', label: 'Email Address' },
            { value: 'phone', label: 'By Phone' },
            { value: 'search', label: 'Search Contact' }
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleChange('contactMethod', opt.value)}
              className={cn(
                "flex-1 py-2 px-3 rounded-lg border text-sm transition-colors",
                formData.contactMethod === opt.value
                  ? "bg-primary/20 border-primary/50 text-primary"
                  : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FormField>
      
      {formData.contactMethod === 'email' && (
        <FormField label="Email Address" required>
          <TextInput
            icon={Mail}
            type="email"
            value={formData.email}
            onChange={v => handleChange('email', v)}
            placeholder="john@example.com"
            data-testid="email-to"
          />
        </FormField>
      )}
      
      {formData.contactMethod === 'phone' && (
        <FormField label="Contact Phone" required hint="We'll find the contact and send to their email">
          <TextInput
            icon={Phone}
            type="tel"
            value={formData.phone}
            onChange={v => handleChange('phone', v)}
            placeholder="(555) 123-4567"
            data-testid="email-phone"
          />
        </FormField>
      )}
      
      {formData.contactMethod === 'search' && (
        <>
          <FormField label="Search Contacts">
            <div className="flex gap-2">
              <TextInput
                value={formData.searchQuery}
                onChange={v => handleChange('searchQuery', v)}
                placeholder="Search by name, email, or phone..."
                data-testid="email-search"
              />
              <Button variant="outline" onClick={searchContacts} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </FormField>
          
          {searchResults.length > 0 && (
            <div className="border border-white/10 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
              {searchResults.map(contact => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => setSelectedContact(contact)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                    selectedContact?.id === contact.id && "bg-primary/10"
                  )}
                >
                  <User className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      {contact.firstName} {contact.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {contact.email || contact.phone}
                    </p>
                  </div>
                  {selectedContact?.id === contact.id && (
                    <CheckCircle className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
          
          {selectedContact && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20">
              <User className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary">
                Sending to: {selectedContact.firstName} {selectedContact.lastName} ({selectedContact.email})
              </span>
            </div>
          )}
        </>
      )}
      
      <FormField label="Subject" required>
        <TextInput
          value={formData.subject}
          onChange={v => handleChange('subject', v)}
          placeholder="Email subject"
          data-testid="email-subject"
        />
      </FormField>
      
      <FormField label="Message" required>
        <TextArea
          value={formData.body}
          onChange={v => handleChange('body', v)}
          placeholder="Type your email message here..."
          rows={6}
          data-testid="email-body"
        />
      </FormField>
      
      <AdvancedSection>
        <FormField label="Reply-To Email">
          <TextInput
            icon={Mail}
            type="email"
            value={formData.replyTo}
            onChange={v => handleChange('replyTo', v)}
            placeholder="replies@yourdomain.com"
            data-testid="email-reply-to"
          />
        </FormField>
      </AdvancedSection>
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 4) ADD NOTE/TASK TO CONTACT WIZARD
// ═══════════════════════════════════════════════════════════════

export function AddNoteTaskWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contactMethod: initialData.contactMethod || 'search',
    email: initialData.email || '',
    phone: initialData.phone || '',
    searchQuery: initialData.searchQuery || '',
    type: initialData.type || 'note', // 'note' or 'task'
    content: initialData.content || '',
    taskTitle: initialData.taskTitle || '',
    dueDate: initialData.dueDate || '',
    dueTime: initialData.dueTime || '',
    assignedTo: initialData.assignedTo || '',
  });
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(initialData.selectedContact || null);
  const [searching, setSearching] = useState(false);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const searchContacts = async () => {
    if (!formData.searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(formData.searchQuery)}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
      }
    } catch (error) {
      toast.error('Error searching contacts');
    } finally {
      setSearching(false);
    }
  };
  
  const handleSubmit = async () => {
    // Validation
    if (formData.type === 'note' && !formData.content.trim()) {
      toast.error('Note content is required');
      return;
    }
    if (formData.type === 'task' && !formData.taskTitle.trim()) {
      toast.error('Task title is required');
      return;
    }
    
    let targetEmail = formData.email;
    let targetPhone = formData.phone;
    
    if (formData.contactMethod === 'search' && selectedContact) {
      targetEmail = selectedContact.email;
      targetPhone = selectedContact.phone;
    }
    
    if (!targetEmail && !targetPhone && !selectedContact) {
      toast.error('Please select a contact');
      return;
    }
    
    setSubmitting(true);
    try {
      const endpoint = formData.type === 'note' 
        ? `${API_URL}/api/wizards/crm/note`
        : `${API_URL}/api/wizards/crm/task`;
      
      const payload = {
        email: targetEmail || undefined,
        phone: targetPhone || undefined,
        contact_id: selectedContact?.id,
        conversation_id: conversationId,
        ...(formData.type === 'note' 
          ? { note: formData.content }
          : { 
              title: formData.taskTitle,
              description: formData.content || undefined,
              due_date: formData.dueDate || undefined,
              due_time: formData.dueTime || undefined,
              assigned_to: formData.assignedTo || undefined
            }
        )
      };
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`${formData.type === 'note' ? 'Note' : 'Task'} added to ${selectedContact?.firstName || 'contact'}`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || result.detail || `Failed to add ${formData.type}`);
      }
    } catch (error) {
      toast.error(`Error adding ${formData.type}: ` + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title={formData.type === 'note' ? 'Add Note' : 'Add Task'}
      subtitle="Add a note or task to a contact"
      icon={FileText}
      iconColor="bg-amber-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Add {formData.type === 'note' ? 'Note' : 'Task'}
            </Button>
          </div>
        </>
      }
    >
      {/* Type Selection */}
      <FormField label="Type" required>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleChange('type', 'note')}
            className={cn(
              "flex-1 py-3 px-4 rounded-xl border text-sm transition-colors flex items-center justify-center gap-2",
              formData.type === 'note'
                ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10"
            )}
          >
            <FileText className="w-4 h-4" />
            Note
          </button>
          <button
            type="button"
            onClick={() => handleChange('type', 'task')}
            className={cn(
              "flex-1 py-3 px-4 rounded-xl border text-sm transition-colors flex items-center justify-center gap-2",
              formData.type === 'task'
                ? "bg-blue-500/20 border-blue-500/50 text-blue-400"
                : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10"
            )}
          >
            <CheckCircle className="w-4 h-4" />
            Task/Reminder
          </button>
        </div>
      </FormField>
      
      {/* Contact Search */}
      <FormField label="Contact" required>
        <div className="flex gap-2">
          <TextInput
            value={formData.searchQuery}
            onChange={v => handleChange('searchQuery', v)}
            placeholder="Search by name, email, or phone..."
            data-testid="note-contact-search"
          />
          <Button variant="outline" onClick={searchContacts} disabled={searching}>
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
      </FormField>
      
      {searchResults.length > 0 && (
        <div className="border border-white/10 rounded-xl overflow-hidden max-h-32 overflow-y-auto">
          {searchResults.map(contact => (
            <button
              key={contact.id}
              type="button"
              onClick={() => setSelectedContact(contact)}
              className={cn(
                "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                selectedContact?.id === contact.id && "bg-primary/10"
              )}
            >
              <User className="w-4 h-4 text-gray-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">
                  {contact.firstName} {contact.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {contact.email || contact.phone}
                </p>
              </div>
              {selectedContact?.id === contact.id && (
                <CheckCircle className="w-4 h-4 text-primary" />
              )}
            </button>
          ))}
        </div>
      )}
      
      {selectedContact && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20">
          <User className="w-4 h-4 text-primary" />
          <span className="text-sm text-primary">
            Adding to: {selectedContact.firstName} {selectedContact.lastName}
          </span>
        </div>
      )}
      
      {/* Note-specific fields */}
      {formData.type === 'note' && (
        <FormField label="Note Content" required>
          <TextArea
            value={formData.content}
            onChange={v => handleChange('content', v)}
            placeholder="Enter your note here..."
            rows={4}
            data-testid="note-content"
          />
        </FormField>
      )}
      
      {/* Task-specific fields */}
      {formData.type === 'task' && (
        <>
          <FormField label="Task Title" required>
            <TextInput
              value={formData.taskTitle}
              onChange={v => handleChange('taskTitle', v)}
              placeholder="e.g., Follow up call"
              data-testid="task-title"
            />
          </FormField>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Due Date" required>
              <TextInput
                type="date"
                value={formData.dueDate}
                onChange={v => handleChange('dueDate', v)}
                data-testid="task-due-date"
              />
            </FormField>
            <FormField label="Due Time">
              <TextInput
                type="time"
                value={formData.dueTime}
                onChange={v => handleChange('dueTime', v)}
                data-testid="task-due-time"
              />
            </FormField>
          </div>
          
          <FormField label="Description (Optional)">
            <TextArea
              value={formData.content}
              onChange={v => handleChange('content', v)}
              placeholder="Additional details..."
              rows={2}
              data-testid="task-description"
            />
          </FormField>
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 5) CREATE OPPORTUNITY WIZARD
// ═══════════════════════════════════════════════════════════════

export function CreateOpportunityWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(initialData.selectedContact || null);
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    contactSearch: initialData.contactSearch || '',
    pipelineId: initialData.pipelineId || '',
    stageId: initialData.stageId || '',
    name: initialData.name || '',
    value: initialData.value || '',
    status: initialData.status || 'open',
    notes: initialData.notes || '',
  });
  
  // Fetch pipelines on mount
  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const response = await fetch(`${API_URL}/api/wizards/crm/pipelines`, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setPipelines(data.pipelines || []);
          
          // Auto-select first pipeline
          if (data.pipelines?.length > 0 && !formData.pipelineId) {
            const firstPipeline = data.pipelines[0];
            setFormData(prev => ({ ...prev, pipelineId: firstPipeline.id }));
            setStages(firstPipeline.stages || []);
          }
        }
      } catch (error) {
        console.error('Error fetching pipelines:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) fetchPipelines();
  }, [isOpen]);
  
  // Update stages when pipeline changes
  useEffect(() => {
    if (formData.pipelineId) {
      const pipeline = pipelines.find(p => p.id === formData.pipelineId);
      setStages(pipeline?.stages || []);
      // Auto-select first stage
      if (pipeline?.stages?.length > 0) {
        setFormData(prev => ({ ...prev, stageId: pipeline.stages[0].id }));
      }
    }
  }, [formData.pipelineId, pipelines]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const searchContacts = async () => {
    if (!formData.contactSearch.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(formData.contactSearch)}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
      }
    } catch (error) {
      toast.error('Error searching contacts');
    } finally {
      setSearching(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedContact) {
      toast.error('Please select a contact');
      return;
    }
    if (!formData.pipelineId) {
      toast.error('Please select a pipeline');
      return;
    }
    if (!formData.stageId) {
      toast.error('Please select a stage');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Opportunity name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/opportunity`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedContact.id,
          pipeline_id: formData.pipelineId,
          stage_id: formData.stageId,
          name: formData.name,
          value: formData.value ? parseFloat(formData.value) : 0,
          status: formData.status,
          notes: formData.notes || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Opportunity "${formData.name}" created`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || result.detail || 'Failed to create opportunity');
      }
    } catch (error) {
      toast.error('Error creating opportunity: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Opportunity"
      subtitle="Create a new deal in your pipeline"
      icon={DollarSign}
      iconColor="bg-green-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Create Opportunity
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Contact Search */}
          <FormField label="Contact" required>
            <div className="flex gap-2">
              <TextInput
                value={formData.contactSearch}
                onChange={v => handleChange('contactSearch', v)}
                placeholder="Search contact..."
                data-testid="opp-contact-search"
              />
              <Button variant="outline" onClick={searchContacts} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </FormField>
          
          {searchResults.length > 0 && (
            <div className="border border-white/10 rounded-xl overflow-hidden max-h-32 overflow-y-auto">
              {searchResults.map(contact => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => {
                    setSelectedContact(contact);
                    // Auto-fill name
                    if (!formData.name) {
                      handleChange('name', `${contact.firstName} ${contact.lastName} - Deal`);
                    }
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                    selectedContact?.id === contact.id && "bg-primary/10"
                  )}
                >
                  <User className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      {contact.firstName} {contact.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {contact.email || contact.phone}
                    </p>
                  </div>
                  {selectedContact?.id === contact.id && (
                    <CheckCircle className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
          
          {selectedContact && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20">
              <User className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary">
                Contact: {selectedContact.firstName} {selectedContact.lastName}
              </span>
            </div>
          )}
          
          {/* Pipeline & Stage */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Pipeline" required>
              <SelectInput
                value={formData.pipelineId}
                onChange={v => handleChange('pipelineId', v)}
                options={pipelines.map(p => ({ value: p.id, label: p.name }))}
                placeholder="Select pipeline"
                data-testid="opp-pipeline"
              />
            </FormField>
            <FormField label="Stage" required>
              <SelectInput
                value={formData.stageId}
                onChange={v => handleChange('stageId', v)}
                options={stages.map(s => ({ value: s.id, label: s.name }))}
                placeholder="Select stage"
                data-testid="opp-stage"
              />
            </FormField>
          </div>
          
          <FormField label="Opportunity Name" required>
            <TextInput
              icon={Briefcase}
              value={formData.name}
              onChange={v => handleChange('name', v)}
              placeholder="e.g., John Smith - Funding Deal"
              data-testid="opp-name"
            />
          </FormField>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Value ($)">
              <TextInput
                icon={DollarSign}
                type="number"
                value={formData.value}
                onChange={v => handleChange('value', v)}
                placeholder="0.00"
                data-testid="opp-value"
              />
            </FormField>
            <FormField label="Status">
              <SelectInput
                value={formData.status}
                onChange={v => handleChange('status', v)}
                options={[
                  { value: 'open', label: 'Open' },
                  { value: 'won', label: 'Won' },
                  { value: 'lost', label: 'Lost' },
                  { value: 'abandoned', label: 'Abandoned' }
                ]}
                data-testid="opp-status"
              />
            </FormField>
          </div>
          
          <FormField label="Notes">
            <TextArea
              value={formData.notes}
              onChange={v => handleChange('notes', v)}
              placeholder="Add any notes about this opportunity..."
              rows={2}
              data-testid="opp-notes"
            />
          </FormField>
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 6) CREATE POD ORDER WIZARD
// ═══════════════════════════════════════════════════════════════

export function CreatePODOrderWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    departmentId: initialData.departmentId || '',
    title: initialData.title || '',
    description: initialData.description || '',
    priority: initialData.priority || 'medium',
    dueDate: initialData.dueDate || '',
    assets: initialData.assets || '',
    brandName: initialData.brandName || '',
    brandColors: initialData.brandColors || '',
    website: initialData.website || '',
  });
  
  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(`${API_URL}/api/departments`, {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setDepartments(data || []);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) fetchDepartments();
  }, [isOpen]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    if (!formData.departmentId) {
      toast.error('Please select a department');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Order title is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Order description is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/pod/order`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          department_id: formData.departmentId,
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          due_date: formData.dueDate || undefined,
          assets: formData.assets || undefined,
          brand_info: {
            business_name: formData.brandName || undefined,
            brand_colors: formData.brandColors || undefined,
            website: formData.website || undefined
          },
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`POD Order #${result.order_number || result.order_id} created`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || result.detail || 'Failed to create POD order');
      }
    } catch (error) {
      toast.error('Error creating order: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create POD Order"
      subtitle="Submit a new Partner on Demand order"
      icon={Package}
      iconColor="bg-purple-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
              Submit Order
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Department" required>
            <SelectInput
              value={formData.departmentId}
              onChange={v => handleChange('departmentId', v)}
              options={departments.map(d => ({ value: d.id, label: d.name }))}
              placeholder="Select department"
              data-testid="pod-department"
            />
          </FormField>
          
          <FormField label="Order Title" required>
            <TextInput
              value={formData.title}
              onChange={v => handleChange('title', v)}
              placeholder="Brief title for your order"
              data-testid="pod-title"
            />
          </FormField>
          
          <FormField label="Requirements / Description" required>
            <TextArea
              value={formData.description}
              onChange={v => handleChange('description', v)}
              placeholder="Describe what you need in detail..."
              rows={4}
              data-testid="pod-description"
            />
          </FormField>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Priority" required>
              <SelectInput
                value={formData.priority}
                onChange={v => handleChange('priority', v)}
                options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Normal' },
                  { value: 'high', label: 'High' }
                ]}
                data-testid="pod-priority"
              />
            </FormField>
            <FormField label="Due Date">
              <TextInput
                type="date"
                value={formData.dueDate}
                onChange={v => handleChange('dueDate', v)}
                data-testid="pod-due-date"
              />
            </FormField>
          </div>
          
          <FormField label="Assets / Links" hint="URLs to reference materials, images, etc.">
            <TextArea
              value={formData.assets}
              onChange={v => handleChange('assets', v)}
              placeholder="Paste URLs to assets, one per line..."
              rows={2}
              data-testid="pod-assets"
            />
          </FormField>
          
          <AdvancedSection>
            <FormField label="Business Name">
              <TextInput
                icon={Building2}
                value={formData.brandName}
                onChange={v => handleChange('brandName', v)}
                placeholder="Your business name"
                data-testid="pod-brand-name"
              />
            </FormField>
            <FormField label="Brand Colors">
              <TextInput
                value={formData.brandColors}
                onChange={v => handleChange('brandColors', v)}
                placeholder="#FF5733, #333333"
                data-testid="pod-brand-colors"
              />
            </FormField>
            <FormField label="Website">
              <TextInput
                icon={Globe}
                value={formData.website}
                onChange={v => handleChange('website', v)}
                placeholder="https://yourwebsite.com"
                data-testid="pod-website"
              />
            </FormField>
          </AdvancedSection>
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 7) DIRECTORY LISTING WIZARD (Enhanced)
// ═══════════════════════════════════════════════════════════════

export function DirectoryListingWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    business_name: initialData.business_name || '',
    category: initialData.category || '',
    subcategories: initialData.subcategories || '',
    description: initialData.description || '',
    services: initialData.services || '',
    phone: initialData.phone || '',
    email: initialData.email || '',
    address: initialData.address || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zip: initialData.zip || '',
    website: initialData.website || '',
    hours: initialData.hours || '',
    serviceArea: initialData.serviceArea || '',
    leadRoutingPipeline: initialData.leadRoutingPipeline || '',
    notificationEmail: initialData.notificationEmail || '',
  });

  const categories = [
    'Business Funding', 'Real Estate', 'Home Services', 'Restaurant', 'Retail', 
    'Professional Services', 'Health & Wellness', 'Automotive', 'Technology', 
    'Education', 'Entertainment', 'Legal', 'Financial Services', 'Insurance', 'Other'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (!formData.business_name.trim()) {
      toast.error('Business name is required');
      return false;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return false;
    }
    if (!formData.city.trim() || !formData.state.trim()) {
      toast.error('City and state are required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/directory/listing`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          business_name: formData.business_name,
          category: formData.category,
          subcategories: formData.subcategories ? formData.subcategories.split(',').map(s => s.trim()) : [],
          description: formData.description,
          services: formData.services ? formData.services.split(',').map(s => s.trim()) : [],
          phone: formData.phone,
          email: formData.email || undefined,
          address: formData.address || undefined,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip || undefined,
          website: formData.website || undefined,
          hours: formData.hours || undefined,
          service_area: formData.serviceArea ? formData.serviceArea.split(',').map(s => s.trim()) : [],
          lead_routing: {
            pipeline: formData.leadRoutingPipeline || undefined,
            notification_email: formData.notificationEmail || undefined
          },
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('Directory listing created successfully!');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || result.detail || 'Failed to create listing');
      }
    } catch (error) {
      toast.error('Error creating listing: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Directory Listing"
      subtitle={`Step ${step} of 2`}
      icon={Building2}
      iconColor="bg-green-500/20"
      footer={
        <>
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          )}
          
          {step < 2 ? (
            <Button onClick={() => validateStep1() && setStep(2)}>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Create Listing
            </Button>
          )}
        </>
      }
    >
      {/* Progress Bar */}
      <div className="h-1 bg-white/10 rounded-full overflow-hidden -mt-2 mb-4">
        <div 
          className="h-full bg-green-500 transition-all duration-300"
          style={{ width: `${(step / 2) * 100}%` }}
        />
      </div>
      
      {step === 1 && (
        <>
          <FormField label="Business Name" required>
            <TextInput
              icon={Building2}
              value={formData.business_name}
              onChange={v => handleChange('business_name', v)}
              placeholder="e.g., One Force Funding"
              data-testid="listing-business-name"
            />
          </FormField>

          <FormField label="Category" required>
            <SelectInput
              value={formData.category}
              onChange={v => handleChange('category', v)}
              options={categories}
              placeholder="Select a category"
              data-testid="listing-category"
            />
          </FormField>
          
          <FormField label="Subcategories" hint="Comma-separated">
            <TextInput
              value={formData.subcategories}
              onChange={v => handleChange('subcategories', v)}
              placeholder="MCA, SBA Loans, Equipment Financing"
              data-testid="listing-subcategories"
            />
          </FormField>

          <FormField label="Description" required>
            <TextArea
              value={formData.description}
              onChange={v => handleChange('description', v)}
              placeholder="Brief description of your business..."
              rows={3}
              data-testid="listing-description"
            />
          </FormField>
          
          <FormField label="Services" required hint="Comma-separated list">
            <TextArea
              value={formData.services}
              onChange={v => handleChange('services', v)}
              placeholder="Working Capital Loans, Equipment Financing, SBA Loans..."
              rows={2}
              data-testid="listing-services"
            />
          </FormField>
        </>
      )}

      {step === 2 && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Phone" required>
              <TextInput
                icon={Phone}
                type="tel"
                value={formData.phone}
                onChange={v => handleChange('phone', v)}
                placeholder="(432) 999-1103"
                data-testid="listing-phone"
              />
            </FormField>
            <FormField label="Email">
              <TextInput
                icon={Mail}
                type="email"
                value={formData.email}
                onChange={v => handleChange('email', v)}
                placeholder="info@business.com"
                data-testid="listing-email"
              />
            </FormField>
          </div>

          <FormField label="Address">
            <TextInput
              icon={MapPin}
              value={formData.address}
              onChange={v => handleChange('address', v)}
              placeholder="123 Main St, Suite 200"
              data-testid="listing-address"
            />
          </FormField>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="City" required>
              <TextInput
                value={formData.city}
                onChange={v => handleChange('city', v)}
                placeholder="Midland"
                data-testid="listing-city"
              />
            </FormField>
            <FormField label="State" required>
              <TextInput
                value={formData.state}
                onChange={v => handleChange('state', v)}
                placeholder="TX"
                maxLength={2}
                className="uppercase"
                data-testid="listing-state"
              />
            </FormField>
            <FormField label="ZIP">
              <TextInput
                value={formData.zip}
                onChange={v => handleChange('zip', v)}
                placeholder="79701"
                data-testid="listing-zip"
              />
            </FormField>
          </div>

          <FormField label="Website">
            <TextInput
              icon={Globe}
              type="url"
              value={formData.website}
              onChange={v => handleChange('website', v)}
              placeholder="https://www.example.com"
              data-testid="listing-website"
            />
          </FormField>

          <FormField label="Business Hours">
            <TextInput
              icon={Clock}
              value={formData.hours}
              onChange={v => handleChange('hours', v)}
              placeholder="Mon-Fri 9am-5pm"
              data-testid="listing-hours"
            />
          </FormField>
          
          <FormField label="Service Area" hint="Cities/ZIP codes, comma-separated">
            <TextInput
              icon={MapPin}
              value={formData.serviceArea}
              onChange={v => handleChange('serviceArea', v)}
              placeholder="Midland, Odessa, 79701, 79705"
              data-testid="listing-service-area"
            />
          </FormField>
          
          <AdvancedSection>
            <FormField label="Lead Routing - CRM Pipeline" hint="Send leads to this pipeline">
              <TextInput
                value={formData.leadRoutingPipeline}
                onChange={v => handleChange('leadRoutingPipeline', v)}
                placeholder="Pipeline ID or name"
                data-testid="listing-lead-pipeline"
              />
            </FormField>
            <FormField label="Notification Email">
              <TextInput
                icon={Mail}
                type="email"
                value={formData.notificationEmail}
                onChange={v => handleChange('notificationEmail', v)}
                placeholder="leads@business.com"
                data-testid="listing-notification-email"
              />
            </FormField>
          </AdvancedSection>
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 8) MOVE OPPORTUNITY STAGE WIZARD
// ═══════════════════════════════════════════════════════════════

export function MoveOpportunityWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    pipelineId: initialData.pipelineId || '',
    searchQuery: initialData.searchQuery || '',
    newStageId: initialData.newStageId || '',
    reason: initialData.reason || '',
  });
  
  // Fetch pipelines on mount
  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const response = await fetch(`${API_URL}/api/wizards/crm/pipelines`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setPipelines(data.pipelines || []);
          if (data.pipelines?.length > 0) {
            const firstPipeline = data.pipelines[0];
            setFormData(prev => ({ ...prev, pipelineId: firstPipeline.id }));
            setStages(firstPipeline.stages || []);
          }
        }
      } catch (error) {
        console.error('Error fetching pipelines:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchPipelines();
  }, [isOpen]);
  
  useEffect(() => {
    if (formData.pipelineId) {
      const pipeline = pipelines.find(p => p.id === formData.pipelineId);
      setStages(pipeline?.stages || []);
    }
  }, [formData.pipelineId, pipelines]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const searchOpportunities = async () => {
    if (!formData.pipelineId) {
      toast.error('Please select a pipeline first');
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/opportunities?pipeline_id=${formData.pipelineId}&q=${encodeURIComponent(formData.searchQuery || '')}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setOpportunities(data.opportunities || []);
      }
    } catch (error) {
      toast.error('Error searching opportunities');
    } finally {
      setSearching(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedOpp) {
      toast.error('Please select an opportunity');
      return;
    }
    if (!formData.newStageId) {
      toast.error('Please select a new stage');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/opportunity/${selectedOpp.id}/stage`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          stage_id: formData.newStageId,
          reason: formData.reason || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Moved "${selectedOpp.name}" to new stage`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to move opportunity');
      }
    } catch (error) {
      toast.error('Error moving opportunity: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Move Opportunity Stage"
      subtitle="Move a deal to a different pipeline stage"
      icon={Briefcase}
      iconColor="bg-amber-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Briefcase className="w-4 h-4 mr-2" />}
              Move Stage
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Pipeline" required>
            <SelectInput
              value={formData.pipelineId}
              onChange={v => handleChange('pipelineId', v)}
              options={pipelines.map(p => ({ value: p.id, label: p.name }))}
              placeholder="Select pipeline"
            />
          </FormField>
          
          <FormField label="Find Opportunity">
            <div className="flex gap-2">
              <TextInput
                value={formData.searchQuery}
                onChange={v => handleChange('searchQuery', v)}
                placeholder="Search by name or contact..."
              />
              <Button variant="outline" onClick={searchOpportunities} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </FormField>
          
          {opportunities.length > 0 && (
            <div className="border border-white/10 rounded-xl overflow-hidden max-h-32 overflow-y-auto">
              {opportunities.map(opp => (
                <button
                  key={opp.id}
                  type="button"
                  onClick={() => setSelectedOpp(opp)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                    selectedOpp?.id === opp.id && "bg-primary/10"
                  )}
                >
                  <DollarSign className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{opp.name}</p>
                    <p className="text-xs text-gray-500">${opp.value?.toLocaleString()} • {opp.stageName}</p>
                  </div>
                  {selectedOpp?.id === opp.id && <CheckCircle className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
          )}
          
          {selectedOpp && (
            <>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Briefcase className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-amber-400">
                  Moving: {selectedOpp.name} (Current: {selectedOpp.stageName})
                </span>
              </div>
              
              <FormField label="Move to Stage" required>
                <SelectInput
                  value={formData.newStageId}
                  onChange={v => handleChange('newStageId', v)}
                  options={stages.filter(s => s.id !== selectedOpp.stageId).map(s => ({ value: s.id, label: s.name }))}
                  placeholder="Select new stage"
                />
              </FormField>
              
              <FormField label="Reason/Notes">
                <TextArea
                  value={formData.reason}
                  onChange={v => handleChange('reason', v)}
                  placeholder="Why is this being moved? (optional)"
                  rows={2}
                />
              </FormField>
            </>
          )}
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 9) CREATE APPOINTMENT WIZARD
// ═══════════════════════════════════════════════════════════════

export function CreateAppointmentWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calendars, setCalendars] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    contactSearch: initialData.contactSearch || '',
    calendarId: initialData.calendarId || '',
    title: initialData.title || '',
    date: initialData.date || '',
    time: initialData.time || '',
    duration: initialData.duration || '30',
    location: initialData.location || '',
    notes: initialData.notes || '',
  });
  
  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        const response = await fetch(`${API_URL}/api/wizards/crm/calendars`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setCalendars(data.calendars || []);
          if (data.calendars?.length > 0) {
            setFormData(prev => ({ ...prev, calendarId: data.calendars[0].id }));
          }
        }
      } catch (error) {
        console.error('Error fetching calendars:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchCalendars();
  }, [isOpen]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const searchContacts = async () => {
    if (!formData.contactSearch.trim()) return;
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(formData.contactSearch)}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
      }
    } catch (error) {
      toast.error('Error searching contacts');
    } finally {
      setSearching(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedContact) {
      toast.error('Please select a contact');
      return;
    }
    if (!formData.calendarId) {
      toast.error('Please select a calendar');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.date || !formData.time) {
      toast.error('Date and time are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/appointment`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedContact.id,
          calendar_id: formData.calendarId,
          title: formData.title,
          date: formData.date,
          time: formData.time,
          duration: parseInt(formData.duration),
          location: formData.location || undefined,
          notes: formData.notes || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Appointment "${formData.title}" scheduled`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create appointment');
      }
    } catch (error) {
      toast.error('Error creating appointment: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Appointment"
      subtitle="Schedule an appointment with a contact"
      icon={Calendar}
      iconColor="bg-blue-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
              Schedule
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Contact" required>
            <div className="flex gap-2">
              <TextInput
                value={formData.contactSearch}
                onChange={v => handleChange('contactSearch', v)}
                placeholder="Search contact..."
              />
              <Button variant="outline" onClick={searchContacts} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </FormField>
          
          {searchResults.length > 0 && (
            <div className="border border-white/10 rounded-xl overflow-hidden max-h-32 overflow-y-auto">
              {searchResults.map(contact => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => setSelectedContact(contact)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                    selectedContact?.id === contact.id && "bg-primary/10"
                  )}
                >
                  <User className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{contact.firstName} {contact.lastName}</p>
                    <p className="text-xs text-gray-500 truncate">{contact.email || contact.phone}</p>
                  </div>
                  {selectedContact?.id === contact.id && <CheckCircle className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
          )}
          
          {selectedContact && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <User className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-400">
                With: {selectedContact.firstName} {selectedContact.lastName}
              </span>
            </div>
          )}
          
          <FormField label="Calendar" required>
            <SelectInput
              value={formData.calendarId}
              onChange={v => handleChange('calendarId', v)}
              options={calendars.map(c => ({ value: c.id, label: c.name }))}
              placeholder="Select calendar"
            />
          </FormField>
          
          <FormField label="Title" required>
            <TextInput
              value={formData.title}
              onChange={v => handleChange('title', v)}
              placeholder="e.g., Discovery Call"
            />
          </FormField>
          
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Date" required>
              <TextInput
                type="date"
                value={formData.date}
                onChange={v => handleChange('date', v)}
              />
            </FormField>
            <FormField label="Time" required>
              <TextInput
                type="time"
                value={formData.time}
                onChange={v => handleChange('time', v)}
              />
            </FormField>
            <FormField label="Duration">
              <SelectInput
                value={formData.duration}
                onChange={v => handleChange('duration', v)}
                options={[
                  { value: '15', label: '15 min' },
                  { value: '30', label: '30 min' },
                  { value: '45', label: '45 min' },
                  { value: '60', label: '1 hour' },
                  { value: '90', label: '1.5 hours' },
                ]}
              />
            </FormField>
          </div>
          
          <FormField label="Location / Meeting Link">
            <TextInput
              value={formData.location}
              onChange={v => handleChange('location', v)}
              placeholder="https://zoom.us/... or address"
            />
          </FormField>
          
          <FormField label="Notes">
            <TextArea
              value={formData.notes}
              onChange={v => handleChange('notes', v)}
              placeholder="Additional details..."
              rows={2}
            />
          </FormField>
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 10) POD REVISION REQUEST WIZARD
// ═══════════════════════════════════════════════════════════════

export function PODRevisionWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  const [formData, setFormData] = useState({
    searchQuery: initialData.searchQuery || '',
    revisionNotes: initialData.revisionNotes || '',
    attachments: initialData.attachments || '',
  });
  
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(`${API_URL}/api/orders?status=in_progress,pending_review&limit=20`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data || []);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchOrders();
  }, [isOpen]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    if (!selectedOrder) {
      toast.error('Please select an order');
      return;
    }
    if (!formData.revisionNotes.trim()) {
      toast.error('Please describe what revisions you need');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/pod/revision`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          order_id: selectedOrder.id,
          revision_notes: formData.revisionNotes,
          attachments: formData.attachments || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Revision request submitted for order #${selectedOrder.order_number}`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to submit revision request');
      }
    } catch (error) {
      toast.error('Error submitting revision: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredOrders = orders.filter(o => 
    !formData.searchQuery || 
    o.order_number?.toLowerCase().includes(formData.searchQuery.toLowerCase()) ||
    o.summary?.toLowerCase().includes(formData.searchQuery.toLowerCase())
  );

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Request Revision"
      subtitle="Request changes to an existing POD order"
      icon={FileText}
      iconColor="bg-purple-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Submit Revision
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Select Order" required>
            <TextInput
              value={formData.searchQuery}
              onChange={v => handleChange('searchQuery', v)}
              placeholder="Search by order number or title..."
            />
          </FormField>
          
          <div className="border border-white/10 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
            {filteredOrders.length > 0 ? (
              filteredOrders.map(order => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => setSelectedOrder(order)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                    selectedOrder?.id === order.id && "bg-purple-500/10"
                  )}
                >
                  <Package className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{order.order_number} - {order.summary}</p>
                    <p className="text-xs text-gray-500">{order.department_name} • {order.status}</p>
                  </div>
                  {selectedOrder?.id === order.id && <CheckCircle className="w-4 h-4 text-purple-400" />}
                </button>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500 text-sm">
                No orders found. You can only request revisions on in-progress orders.
              </div>
            )}
          </div>
          
          {selectedOrder && (
            <>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <Package className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-purple-400">
                  Revising: {selectedOrder.order_number}
                </span>
              </div>
              
              <FormField label="Revision Notes" required hint="Describe the changes you need">
                <TextArea
                  value={formData.revisionNotes}
                  onChange={v => handleChange('revisionNotes', v)}
                  placeholder="Please describe what changes or corrections are needed..."
                  rows={4}
                />
              </FormField>
              
              <FormField label="Reference Links" hint="URLs to examples, screenshots, etc.">
                <TextArea
                  value={formData.attachments}
                  onChange={v => handleChange('attachments', v)}
                  placeholder="Paste URLs to reference materials, one per line..."
                  rows={2}
                />
              </FormField>
            </>
          )}
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 11) WORKFLOW DEPLOY WIZARD
// ═══════════════════════════════════════════════════════════════

export function WorkflowDeployWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    recipe: initialData.recipe || '',
    pipelineId: initialData.pipelineId || '',
    calendarId: initialData.calendarId || '',
    fromNumber: initialData.fromNumber || '',
    activateNow: initialData.activateNow ?? true,
  });
  
  const recipes = [
    { value: 'missed_call_textback', label: 'Missed Call Text-Back', description: 'Auto-text when a call is missed' },
    { value: 'lead_followup', label: 'Lead Follow-up Sequence', description: 'Multi-step follow-up for new leads' },
    { value: 'review_request', label: 'Review Request', description: 'Request reviews after service' },
    { value: 'appointment_reminder', label: 'Appointment Reminder', description: 'Send reminders before appointments' },
    { value: 'birthday_campaign', label: 'Birthday Campaign', description: 'Auto birthday wishes' },
  ];
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    if (!formData.recipe) {
      toast.error('Please select a recipe');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/workflow-deploy`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          recipe: formData.recipe,
          pipeline_id: formData.pipelineId || undefined,
          calendar_id: formData.calendarId || undefined,
          from_number: formData.fromNumber || undefined,
          activate_now: formData.activateNow,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Workflow "${recipes.find(r => r.value === formData.recipe)?.label}" deployed!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to deploy workflow');
      }
    } catch (error) {
      toast.error('Error deploying workflow: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Deploy Workflow Recipe"
      subtitle="Set up an automation recipe"
      icon={Zap}
      iconColor="bg-amber-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Deploy Workflow
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Select Recipe" required>
        <div className="space-y-2">
          {recipes.map(recipe => (
            <button
              key={recipe.value}
              type="button"
              onClick={() => handleChange('recipe', recipe.value)}
              className={cn(
                "w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left",
                formData.recipe === recipe.value
                  ? "bg-amber-500/20 border-amber-500/50 text-white"
                  : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10"
              )}
            >
              <Zap className={cn("w-5 h-5", formData.recipe === recipe.value ? "text-amber-400" : "text-gray-500")} />
              <div className="flex-1">
                <p className="font-medium">{recipe.label}</p>
                <p className="text-xs text-gray-500">{recipe.description}</p>
              </div>
              {formData.recipe === recipe.value && <CheckCircle className="w-5 h-5 text-amber-400" />}
            </button>
          ))}
        </div>
      </FormField>
      
      <FormField label="Activate Now?">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleChange('activateNow', true)}
            className={cn(
              "flex-1 py-3 rounded-xl border transition-colors",
              formData.activateNow
                ? "bg-green-500/20 border-green-500/50 text-green-400"
                : "bg-white/5 border-white/10 text-gray-400"
            )}
          >
            Yes, activate now
          </button>
          <button
            type="button"
            onClick={() => handleChange('activateNow', false)}
            className={cn(
              "flex-1 py-3 rounded-xl border transition-colors",
              !formData.activateNow
                ? "bg-red-500/20 border-red-500/50 text-red-400"
                : "bg-white/5 border-white/10 text-gray-400"
            )}
          >
            Save as draft
          </button>
        </div>
      </FormField>
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 12) PURCHASE PHONE NUMBER WIZARD
// ═══════════════════════════════════════════════════════════════

export function PurchasePhoneWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  
  const [formData, setFormData] = useState({
    country: initialData.country || 'US',
    areaCode: initialData.areaCode || '',
    capabilities: initialData.capabilities || ['sms', 'voice'],
    confirmPurchase: false,
  });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const searchNumbers = async () => {
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-phone-numbers?country=${formData.country}&area_code=${formData.areaCode}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableNumbers(data.numbers || []);
      }
    } catch (error) {
      toast.error('Error searching numbers');
    } finally {
      setSearching(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!selectedNumber) {
      toast.error('Please select a phone number');
      return;
    }
    if (!formData.confirmPurchase) {
      toast.error('Please confirm the purchase');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/purchase-phone`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          phone_number: selectedNumber.phoneNumber,
          capabilities: formData.capabilities,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Phone number ${selectedNumber.phoneNumber} purchased!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to purchase number');
      }
    } catch (error) {
      toast.error('Error purchasing number: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Purchase Phone Number"
      subtitle="Buy a new phone number for your account"
      icon={Phone}
      iconColor="bg-green-500/20"
      footer={
        <>
          <div className="text-xs text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            This is a billable action
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !selectedNumber || !formData.confirmPurchase}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
              Purchase Number
            </Button>
          </div>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Country">
          <SelectInput
            value={formData.country}
            onChange={v => handleChange('country', v)}
            options={[
              { value: 'US', label: 'United States' },
              { value: 'CA', label: 'Canada' },
              { value: 'UK', label: 'United Kingdom' },
            ]}
          />
        </FormField>
        <FormField label="Area Code">
          <TextInput
            value={formData.areaCode}
            onChange={v => handleChange('areaCode', v)}
            placeholder="e.g., 432"
            maxLength={3}
          />
        </FormField>
      </div>
      
      <FormField label="Capabilities">
        <div className="flex gap-2">
          {['sms', 'voice'].map(cap => (
            <button
              key={cap}
              type="button"
              onClick={() => {
                const caps = formData.capabilities.includes(cap)
                  ? formData.capabilities.filter(c => c !== cap)
                  : [...formData.capabilities, cap];
                handleChange('capabilities', caps);
              }}
              className={cn(
                "flex-1 py-2 rounded-lg border transition-colors uppercase text-sm",
                formData.capabilities.includes(cap)
                  ? "bg-primary/20 border-primary/50 text-primary"
                  : "bg-white/5 border-white/10 text-gray-400"
              )}
            >
              {cap}
            </button>
          ))}
        </div>
      </FormField>
      
      <Button variant="outline" onClick={searchNumbers} disabled={searching} className="w-full">
        {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
        Search Available Numbers
      </Button>
      
      {availableNumbers.length > 0 && (
        <div className="border border-white/10 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
          {availableNumbers.map(num => (
            <button
              key={num.phoneNumber}
              type="button"
              onClick={() => setSelectedNumber(num)}
              className={cn(
                "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                selectedNumber?.phoneNumber === num.phoneNumber && "bg-primary/10"
              )}
            >
              <Phone className="w-4 h-4 text-gray-500" />
              <div className="flex-1">
                <p className="text-sm text-white font-mono">{num.phoneNumber}</p>
                <p className="text-xs text-gray-500">{num.locality}, {num.region}</p>
              </div>
              {selectedNumber?.phoneNumber === num.phoneNumber && <CheckCircle className="w-4 h-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
      
      {selectedNumber && (
        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.confirmPurchase}
              onChange={e => handleChange('confirmPurchase', e.target.checked)}
              className="w-4 h-4 rounded border-white/20"
            />
            <span className="text-sm text-amber-200">
              I confirm this purchase. Monthly charges will apply to my account.
            </span>
          </label>
        </div>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 13) DIRECTORY LEAD FOLLOW-UP SETUP WIZARD (Cross-system)
// ═══════════════════════════════════════════════════════════════

export function DirectoryLeadFollowupWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  
  const [formData, setFormData] = useState({
    listingId: initialData.listingId || '',
    pipelineId: initialData.pipelineId || '',
    stageId: initialData.stageId || '',
    autoTag: initialData.autoTag || 'Directory Lead',
    sendSms: initialData.sendSms ?? true,
    smsMessage: initialData.smsMessage || 'Thanks for your interest! We received your inquiry and will get back to you shortly.',
    sendEmail: initialData.sendEmail ?? false,
    assignTo: initialData.assignTo || '',
  });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch listings
        const listingsRes = await fetch(`${API_URL}/api/directory/my-listings`, {
          headers: getAuthHeaders()
        });
        if (listingsRes.ok) {
          const data = await listingsRes.json();
          setListings(data.listings || []);
        }
        
        // Fetch pipelines
        const pipelinesRes = await fetch(`${API_URL}/api/wizards/crm/pipelines`, {
          headers: getAuthHeaders()
        });
        if (pipelinesRes.ok) {
          const data = await pipelinesRes.json();
          setPipelines(data.pipelines || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchData();
  }, [isOpen]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    if (!formData.listingId) {
      toast.error('Please select a listing');
      return;
    }
    if (!formData.pipelineId) {
      toast.error('Please select a CRM pipeline');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/power/directory-lead-followup`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          listing_id: formData.listingId,
          pipeline_id: formData.pipelineId,
          stage_id: formData.stageId || undefined,
          auto_tag: formData.autoTag || undefined,
          send_sms: formData.sendSms,
          sms_message: formData.smsMessage || undefined,
          send_email: formData.sendEmail,
          assign_to: formData.assignTo || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('Directory lead follow-up automation configured!');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to configure automation');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPipeline = pipelines.find(p => p.id === formData.pipelineId);

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Directory Lead Follow-up"
      subtitle="Auto-route directory leads to your CRM"
      icon={Globe}
      iconColor="bg-amber-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Configure Automation
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Directory Listing" required>
            <SelectInput
              value={formData.listingId}
              onChange={v => handleChange('listingId', v)}
              options={listings.map(l => ({ value: l.id, label: l.name }))}
              placeholder="Select a listing"
            />
          </FormField>
          
          <FormField label="Send Leads to Pipeline" required>
            <SelectInput
              value={formData.pipelineId}
              onChange={v => handleChange('pipelineId', v)}
              options={pipelines.map(p => ({ value: p.id, label: p.name }))}
              placeholder="Select pipeline"
            />
          </FormField>
          
          {selectedPipeline && (
            <FormField label="Initial Stage">
              <SelectInput
                value={formData.stageId}
                onChange={v => handleChange('stageId', v)}
                options={selectedPipeline.stages?.map(s => ({ value: s.id, label: s.name })) || []}
                placeholder="First stage (default)"
              />
            </FormField>
          )}
          
          <FormField label="Auto-Tag New Leads">
            <TextInput
              icon={Tag}
              value={formData.autoTag}
              onChange={v => handleChange('autoTag', v)}
              placeholder="e.g., Directory Lead"
            />
          </FormField>
          
          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.sendSms}
                onChange={e => handleChange('sendSms', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-white">Send immediate SMS</p>
                <p className="text-xs text-gray-500">Auto-respond to new leads</p>
              </div>
            </label>
            
            {formData.sendSms && (
              <TextArea
                value={formData.smsMessage}
                onChange={v => handleChange('smsMessage', v)}
                placeholder="SMS message..."
                rows={2}
              />
            )}
          </div>
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// 14) NEW CLIENT SETUP WIZARD (Cross-system)
// ═══════════════════════════════════════════════════════════════

export function NewClientSetupWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Business Info
    businessName: initialData.businessName || '',
    contactName: initialData.contactName || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    // Step 2: Directory Listing
    createListing: initialData.createListing ?? true,
    category: initialData.category || '',
    description: initialData.description || '',
    // Step 3: CRM Setup
    createPipeline: initialData.createPipeline ?? false,
    assignedUser: initialData.assignedUser || '',
    // Step 4: Phone & Review
    purchaseNumber: initialData.purchaseNumber ?? false,
    installReviewSystem: initialData.installReviewSystem ?? true,
  });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    if (!formData.businessName.trim()) {
      toast.error('Business name is required');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/power/new-client-setup`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('New client setup completed!');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to complete setup');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="New Client Setup"
      subtitle={`Step ${step} of 4 - Complete client onboarding`}
      icon={User}
      iconColor="bg-amber-500/20"
      footer={
        <>
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          )}
          
          {step < 4 ? (
            <Button onClick={() => setStep(step + 1)}>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Complete Setup
            </Button>
          )}
        </>
      }
    >
      {/* Progress Bar */}
      <div className="h-1 bg-white/10 rounded-full overflow-hidden -mt-2 mb-4">
        <div 
          className="h-full bg-amber-500 transition-all duration-300"
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>
      
      {step === 1 && (
        <>
          <h3 className="text-sm font-medium text-gray-300 mb-4">Business Information</h3>
          
          <FormField label="Business Name" required>
            <TextInput
              icon={Building2}
              value={formData.businessName}
              onChange={v => handleChange('businessName', v)}
              placeholder="e.g., One Force Funding"
            />
          </FormField>
          
          <FormField label="Primary Contact Name">
            <TextInput
              icon={User}
              value={formData.contactName}
              onChange={v => handleChange('contactName', v)}
              placeholder="e.g., John Smith"
            />
          </FormField>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Email" required>
              <TextInput
                icon={Mail}
                type="email"
                value={formData.email}
                onChange={v => handleChange('email', v)}
                placeholder="email@business.com"
              />
            </FormField>
            <FormField label="Phone">
              <TextInput
                icon={Phone}
                type="tel"
                value={formData.phone}
                onChange={v => handleChange('phone', v)}
                placeholder="(555) 123-4567"
              />
            </FormField>
          </div>
        </>
      )}
      
      {step === 2 && (
        <>
          <h3 className="text-sm font-medium text-gray-300 mb-4">Directory Listing</h3>
          
          <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={formData.createListing}
              onChange={e => handleChange('createListing', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <div className="flex-1">
              <p className="text-sm text-white">Create directory listing</p>
              <p className="text-xs text-gray-500">List this business in the AI Horse-Powered Directory</p>
            </div>
          </label>
          
          {formData.createListing && (
            <>
              <FormField label="Category">
                <SelectInput
                  value={formData.category}
                  onChange={v => handleChange('category', v)}
                  options={['Business Funding', 'Real Estate', 'Home Services', 'Professional Services', 'Other']}
                  placeholder="Select category"
                />
              </FormField>
              
              <FormField label="Description">
                <TextArea
                  value={formData.description}
                  onChange={v => handleChange('description', v)}
                  placeholder="Brief business description..."
                  rows={3}
                />
              </FormField>
            </>
          )}
        </>
      )}
      
      {step === 3 && (
        <>
          <h3 className="text-sm font-medium text-gray-300 mb-4">CRM Setup</h3>
          
          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
              <input
                type="checkbox"
                checked={true}
                disabled
                className="w-4 h-4 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-white">Create CRM contact</p>
                <p className="text-xs text-gray-500">Add client to your CRM (automatic)</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.createPipeline}
                onChange={e => handleChange('createPipeline', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-white">Create opportunity</p>
                <p className="text-xs text-gray-500">Add to your default pipeline</p>
              </div>
            </label>
          </div>
        </>
      )}
      
      {step === 4 && (
        <>
          <h3 className="text-sm font-medium text-gray-300 mb-4">Additional Options</h3>
          
          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.purchaseNumber}
                onChange={e => handleChange('purchaseNumber', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-white">Purchase dedicated phone number</p>
                <p className="text-xs text-amber-400">Additional monthly charges apply</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.installReviewSystem}
                onChange={e => handleChange('installReviewSystem', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <div className="flex-1">
                <p className="text-sm text-white">Enable review request automation</p>
                <p className="text-xs text-gray-500">Auto-request reviews after service</p>
              </div>
            </label>
          </div>
          
          <div className="mt-4 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
            <p className="text-sm text-green-400 font-medium">Ready to complete setup!</p>
            <p className="text-xs text-gray-400 mt-1">
              This will create: CRM contact{formData.createListing ? ', directory listing' : ''}{formData.createPipeline ? ', pipeline opportunity' : ''}
            </p>
          </div>
        </>
      )}
    </WizardModal>
  );
}


// ═══════════════════════════════════════════════════════════════
// WIZARD CONTROLLER - Main export for managing wizard state
// ═══════════════════════════════════════════════════════════════

// Import advanced CRM wizards
import {
  SendProposalWizard,
  CreateInvoiceWizard,
  CreatePaymentLinkWizard,
  SendReviewRequestWizard,
  CreateReviewCampaignWizard,
  CreateChatWidgetWizard,
  CreateAIChatbotWizard,
  CreateAIAgentWizard,
} from './AdvancedCRMWizards';

// Import more wizards
import {
  SendInternalMessageWizard,
  CheckUnreadMessagesWizard,
  AssignConversationWizard,
  DashboardSnapshotWizard,
  RevenueReportWizard,
  CampaignReportWizard,
  CreateCustomFieldWizard,
  CreateTagWizard,
  CreateUserWizard,
  CreatePipelineWizard,
  BulkSMSWizard,
  BulkTagWizard,
  OrderStatusWizard,
  AssetGeneratorWizard,
  UpdateListingWizard,
  AddOfferWizard,
  ListingReportWizard,
} from './MoreWizards';

// Import Sales Lead wizards
import {
  SearchLeadWizard,
  CallLeadWizard,
  TextLeadWizard,
  EmailLeadWizard,
  BookLeadAppointmentWizard,
  SendLeadAgreementWizard,
  SendLeadEstimateWizard,
  SendLeadInvoiceWizard,
  SendLeadPaymentLinkWizard,
  SendLeadSubscriptionWizard,
  CreateLeadWizard,
  SearchLeadPipelinesWizard,
} from './SalesLeadWizards';

export function WizardController({ activeWizard, onClose, onSuccess, initialData = {}, conversationId }) {
  const wizardProps = { isOpen: true, onClose, onSuccess, initialData, conversationId };
  
  switch (activeWizard) {
    // Sales Lead (Priority Section)
    case 'create_lead':
      return <CreateLeadWizard {...wizardProps} />;
    case 'search_lead':
      return <SearchLeadWizard {...wizardProps} />;
    case 'call_lead':
      return <CallLeadWizard {...wizardProps} />;
    case 'text_lead':
      return <TextLeadWizard {...wizardProps} />;
    case 'email_lead':
      return <EmailLeadWizard {...wizardProps} />;
    case 'book_lead_appointment':
      return <BookLeadAppointmentWizard {...wizardProps} />;
    case 'send_lead_agreement':
      return <SendLeadAgreementWizard {...wizardProps} />;
    case 'send_lead_estimate':
      return <SendLeadEstimateWizard {...wizardProps} />;
    case 'send_lead_invoice':
      return <SendLeadInvoiceWizard {...wizardProps} />;
    case 'send_lead_payment_link':
      return <SendLeadPaymentLinkWizard {...wizardProps} />;
    case 'send_lead_subscription':
      return <SendLeadSubscriptionWizard {...wizardProps} />;
    case 'search_lead_pipelines':
      return <SearchLeadPipelinesWizard {...wizardProps} />;
    
    // Core CRM
    case 'search_contact':
      return <SearchContactWizard {...wizardProps} />;
    case 'create_contact':
      return <CreateContactWizard {...wizardProps} />;
    case 'update_contact':
      return <UpdateContactWizard {...wizardProps} />;
    case 'send_sms':
      return <SendSMSWizard {...wizardProps} />;
    case 'send_email':
      return <SendEmailWizard {...wizardProps} />;
    case 'add_note_task':
      return <AddNoteTaskWizard {...wizardProps} />;
    case 'create_opportunity':
      return <CreateOpportunityWizard {...wizardProps} />;
    case 'move_opportunity':
      return <MoveOpportunityWizard {...wizardProps} />;
    case 'create_appointment':
      return <CreateAppointmentWizard {...wizardProps} />;
    case 'workflow_deploy':
      return <WorkflowDeployWizard {...wizardProps} />;
    case 'purchase_phone':
      return <PurchasePhoneWizard {...wizardProps} />;
    
    // Payments / Billing (G)
    case 'send_proposal':
      return <SendProposalWizard {...wizardProps} />;
    case 'create_invoice':
      return <CreateInvoiceWizard {...wizardProps} />;
    case 'create_payment_link':
      return <CreatePaymentLinkWizard {...wizardProps} />;
    
    // Reviews / Reputation (H)
    case 'send_review_request':
      return <SendReviewRequestWizard {...wizardProps} />;
    case 'create_review_campaign':
      return <CreateReviewCampaignWizard {...wizardProps} />;
    
    // AI / Chatbots / Automations (I)
    case 'create_chat_widget':
      return <CreateChatWidgetWizard {...wizardProps} />;
    case 'create_ai_chatbot':
      return <CreateAIChatbotWizard {...wizardProps} />;
    case 'create_ai_agent':
      return <CreateAIAgentWizard {...wizardProps} />;
    
    // Conversations (J)
    case 'send_internal_message':
      return <SendInternalMessageWizard {...wizardProps} />;
    case 'check_unread_messages':
      return <CheckUnreadMessagesWizard {...wizardProps} />;
    case 'assign_conversation':
      return <AssignConversationWizard {...wizardProps} />;
    
    // Reporting (K)
    case 'dashboard_snapshot':
      return <DashboardSnapshotWizard {...wizardProps} />;
    case 'revenue_report':
      return <RevenueReportWizard {...wizardProps} />;
    case 'campaign_report':
      return <CampaignReportWizard {...wizardProps} />;
    
    // Admin Tools (L)
    case 'create_custom_field':
      return <CreateCustomFieldWizard {...wizardProps} />;
    case 'create_tag':
      return <CreateTagWizard {...wizardProps} />;
    case 'create_user':
      return <CreateUserWizard {...wizardProps} />;
    case 'create_pipeline':
      return <CreatePipelineWizard {...wizardProps} />;
    
    // Bulk Actions (M)
    case 'bulk_sms':
      return <BulkSMSWizard {...wizardProps} />;
    case 'bulk_tag':
      return <BulkTagWizard {...wizardProps} />;
    
    // POD
    case 'create_pod_order':
      return <CreatePODOrderWizard {...wizardProps} />;
    case 'revision_request':
      return <PODRevisionWizard {...wizardProps} />;
    case 'order_status':
      return <OrderStatusWizard {...wizardProps} />;
    case 'asset_generator':
      return <AssetGeneratorWizard {...wizardProps} />;
    
    // Directory
    case 'create_listing':
      return <DirectoryListingWizard {...wizardProps} />;
    case 'update_listing':
      return <UpdateListingWizard {...wizardProps} />;
    case 'add_offer':
      return <AddOfferWizard {...wizardProps} />;
    case 'listing_report':
      return <ListingReportWizard {...wizardProps} />;
    
    // Power Wizards
    case 'directory_lead_followup':
      return <DirectoryLeadFollowupWizard {...wizardProps} />;
    case 'new_client_setup':
      return <NewClientSetupWizard {...wizardProps} />;
    
    default:
      return null;
  }
}

export default WizardController;
